import { useState, useEffect, useRef } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { ChevronDown } from "lucide-react"
import { cn } from "@/lib/utils"
import type { SharedReportListItem } from "@/types/report"

interface SaveViewDialogProps {
  open: boolean
  onClose: () => void
  onSave: (name: string, description: string, existingStateId?: number) => void | Promise<void>
  views: SharedReportListItem[]
  activeViewId: number | null
}

export function SaveViewDialog({
  open,
  onClose,
  onSave,
  views,
  activeViewId,
}: SaveViewDialogProps) {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [selectedViewId, setSelectedViewId] = useState<number | null>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      if (activeViewId) {
        const view = views.find((v) => v.userProfileSearchStateId === activeViewId)
        setName(view?.stateName ?? "")
        setDescription(view?.stateDescription ?? "")
        setSelectedViewId(activeViewId)
      } else {
        setName("")
        setDescription("")
        setSelectedViewId(null)
      }
      setDropdownOpen(false)
    }
  }, [open, activeViewId, views])

  const handleSelectView = (view: SharedReportListItem) => {
    setName(view.stateName)
    setDescription(view.stateDescription ?? "")
    setSelectedViewId(view.userProfileSearchStateId)
    setDropdownOpen(false)
    inputRef.current?.focus()
  }

  const handleNameChange = (value: string) => {
    setName(value)
    // If user types a different name, clear the selected view (treat as new)
    if (selectedViewId) {
      const view = views.find((v) => v.userProfileSearchStateId === selectedViewId)
      if (view && value !== view.stateName) {
        setSelectedViewId(null)
      }
    }
  }

  const handleSave = async () => {
    if (!name.trim()) return
    await onSave(name.trim(), description.trim(), selectedViewId ?? undefined)
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            Save View
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="view-name" className="text-xs font-medium">
              View Name <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                ref={inputRef}
                id="view-name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Type a new name or choose an existing one"
                className="h-9 text-sm pr-8"
                autoFocus
              />
              {views.length > 0 && (
                <button
                  type="button"
                  className="absolute right-0 top-0 h-9 w-8 flex items-center justify-center text-slate-400 hover:text-slate-600"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  aria-label="Show existing views"
                >
                  <ChevronDown className="w-4 h-4" />
                </button>
              )}
              {dropdownOpen && views.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-md max-h-40 overflow-y-auto">
                  {views.map((view) => (
                    <button
                      key={view.userProfileSearchStateId}
                      type="button"
                      className={cn(
                        "w-full text-left px-3 py-1.5 text-sm hover:bg-slate-100 transition-colors",
                        selectedViewId === view.userProfileSearchStateId && "bg-slate-100 font-medium"
                      )}
                      onClick={() => handleSelectView(view)}
                    >
                      {view.stateName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="view-description" className="text-xs font-medium">Description</Label>
            <textarea
              id="view-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder=""
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-y min-h-[60px]"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" className="text-xs" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" className="text-xs" onClick={handleSave} disabled={!name.trim()}>
            {selectedViewId ? "Update" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
